import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import "./dashboard.css";
import { vendorApi } from "../../services/vendorApi";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function money(n) {
  const x = Number(n || 0);
  return "₹" + x.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function safeMonths(arr) {
  const map = new Map();
  (Array.isArray(arr) ? arr : []).forEach((x) => {
    const name = String(x?.name || "");
    const orders = Number(x?.orders || 0);
    if (name) map.set(name, Number.isFinite(orders) ? orders : 0);
  });
  return MONTHS.map((m) => ({ name: m, orders: map.has(m) ? map.get(m) : 0 }));
}

function calcTop(data) {
  let best = { name: "-", orders: 0 };
  for (const x of data) {
    const v = Number(x?.orders || 0);
    if (v > best.orders) best = { name: x.name, orders: v };
  }
  return best;
}

function calcAvg(data) {
  if (!data?.length) return 0;
  const sum = data.reduce((a, b) => a + Number(b?.orders || 0), 0);
  return Math.round(sum / data.length);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="vd-tooltip">
        <div className="vd-tooltip-label">{label}</div>
        <div className="vd-tooltip-value">{payload[0].value} Orders</div>
      </div>
    );
  }
  return null;
};

function StatSkeleton() {
  return (
    <div className="vd-stat vd-stat-skeleton">
      <div className="vd-skeleton vd-skeleton-sm" />
      <div className="vd-skeleton vd-skeleton-lg" style={{ marginTop: 12 }} />
      <div className="vd-skeleton vd-skeleton-bar" style={{ marginTop: 14 }} />
    </div>
  );
}

export default function Dashboard() {
  const token = useMemo(
    () => localStorage.getItem("vendor_token") || localStorage.getItem("token") || "",
    []
  );

  const vendorId = useMemo(() => {
    // First try plain vendor_id key
    const plain = Number(localStorage.getItem("vendor_id") || 0);
    if (plain > 0) return plain;

    // Fallback: read from vendor_me JSON object ({"id":45,"vendor_id":45,...})
    try {
      const me = JSON.parse(localStorage.getItem("vendor_me") || "{}");
      const id = Number(me?.vendor_id ?? me?.id ?? 0);
      return id > 0 ? id : null;
    } catch {
      return null;
    }
  }, []);

  const vendorName = useMemo(() => {
    // First try plain key
    const plain = localStorage.getItem("vendor_name");
    if (plain) return plain;

    // Fallback: read from vendor_me JSON object
    try {
      const me = JSON.parse(localStorage.getItem("vendor_me") || "{}");
      return me?.name || me?.vendor_name || "Vendor";
    } catch {
      return "Vendor";
    }
  }, []);

  const [stats, setStats] = useState({
    totalProducts: null,
    totalOrders:   null,
    pendingOrders: null,
    walletBalance: null,
  });

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsErr,     setStatsErr]     = useState("");

  const [view,         setView]         = useState("12m");
  const [chartData,    setChartData]    = useState(MONTHS.map((m) => ({ name: m, orders: 0 })));
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartErr,     setChartErr]     = useState("");

  const filtered = useMemo(
    () => (view === "6m" ? chartData.slice(6) : chartData),
    [view, chartData]
  );
  const top = useMemo(() => calcTop(filtered), [filtered]);
  const avg = useMemo(() => calcAvg(filtered), [filtered]);

  // ── Wallet only refresh ──────────────────────────────────
  const loadWallet = useCallback(async () => {
    try {
      const res = await vendorApi.wallet(token);
      const wb = Number(
        res?.balance             ??
        res?.wallet_balance      ??
        res?.data?.balance       ??
        res?.data?.wallet_balance ??
        0
      );
      setStats((p) => ({ ...p, walletBalance: Number.isFinite(wb) ? wb : 0 }));
    } catch (e) {
      console.error("Wallet refresh failed:", e);
    }
  }, [token]);

  // ── Full stats load ──────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!token || !vendorId) {
      setStatsErr("Session error: please log in again.");
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsErr("");

    try {
      const [walletRes, productsRes, ordersRes] = await Promise.allSettled([
        vendorApi.wallet(token),
        vendorApi.products(token, vendorId),
        vendorApi.orders(token, vendorId),
      ]);

      // ── Wallet ──────────────────────────────────────────
      let walletBalance = 0;
      if (walletRes.status === "fulfilled") {
        const w = walletRes.value;
        const wb = Number(
          w?.balance             ??
          w?.wallet_balance      ??
          w?.data?.balance       ??
          w?.data?.wallet_balance ??
          0
        );
        walletBalance = Number.isFinite(wb) ? wb : 0;
      }

      // ── Products ─────────────────────────────────────────
      // products/list.php returns: { success: true, products: [...], count: N }
      // ✅ Use "count" directly — most reliable since PHP sets it
      let totalProducts = 0;
      if (productsRes.status === "fulfilled") {
        const p = productsRes.value;
        console.log("📦 Products API response:", p); // debug — remove after fix confirmed
        if (p?.count !== undefined) {
          // ✅ PHP explicitly returns count — use it
          totalProducts = Number(p.count);
        } else if (Array.isArray(p?.products)) {
          totalProducts = p.products.length;
        } else if (Array.isArray(p?.data)) {
          totalProducts = p.data.length;
        } else if (Array.isArray(p)) {
          totalProducts = p.length;
        }
      }

      // ── Orders ───────────────────────────────────────────
      // orders/list.php returns: { success: true, orders: [...] }
      // ✅ Read from "orders" key, count total and filter pending
      let totalOrders   = 0;
      let pendingOrders = 0;
      if (ordersRes.status === "fulfilled") {
        const o = ordersRes.value;
        console.log("🛒 Orders API response:", o); // debug — remove after fix confirmed

        // Build the list from the correct key
        const list = Array.isArray(o?.orders)
          ? o.orders          // ✅ orders/list.php uses "orders" key
          : Array.isArray(o?.data)
          ? o.data
          : Array.isArray(o)
          ? o
          : [];

        totalOrders   = list.length;
        pendingOrders = list.filter(
          (x) => String(x?.status || "").toLowerCase() === "pending"
        ).length;
      }

      setStats({ walletBalance, totalProducts, totalOrders, pendingOrders });

    } catch (e) {
      setStatsErr(String(e?.message || e));
      setStats({ walletBalance: 0, totalProducts: 0, totalOrders: 0, pendingOrders: 0 });
    } finally {
      setStatsLoading(false);
    }
  }, [token, vendorId]);

  // ── Monthly chart ────────────────────────────────────────
  const loadMonthly = useCallback(async () => {
    if (!token || !vendorId) return;
    setChartErr("");
    setLoadingChart(true);
    try {
      const res = await vendorApi.monthlyOrders(token, new Date().getFullYear());
      setChartData(safeMonths(res?.data));
    } catch (e) {
      setChartErr(String(e?.message || e));
      setChartData(MONTHS.map((m) => ({ name: m, orders: 0 })));
    } finally {
      setLoadingChart(false);
    }
  }, [token, vendorId]);

  const refreshAll = useCallback(() => {
    loadStats();
    loadMonthly();
  }, [loadStats, loadMonthly]);

  useEffect(() => {
    loadStats();
    loadMonthly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // ── Session error screen ─────────────────────────────────
  if (!token || !vendorId) {
    return (
      <div className="vd-wrap">
        <div className="vd-container">
          <div className="vd-err" style={{ marginTop: 40, textAlign: "center", padding: "24px 20px" }}>
            <strong>Session expired or vendor not found.</strong><br />
            Please <a href="/yuroshopy/vendor/login">log in again</a>.
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Products",
      value: stats.totalProducts,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
        </svg>
      ),
      color: "blue",
    },
    {
      title: "Total Orders",
      value: stats.totalOrders,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 01-8 0"/>
        </svg>
      ),
      color: "indigo",
    },
    {
      title: "Pending Orders",
      value: stats.pendingOrders,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      color: "amber",
    },
  ];

  return (
    <div className="vd-wrap">
      <div className="vd-container">

        {/* ── Header ── */}
        <div className="vd-header">
          <div>
            <h1 className="vd-page-title">Dashboard</h1>
            <p className="vd-page-sub">
              Welcome back, <strong>{vendorName}</strong>! Here's what's happening with your store.
            </p>
          </div>
          <div className="vd-header-actions">
            <button
              className="vd-refresh-btn"
              onClick={refreshAll}
              disabled={statsLoading || loadingChart}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
              </svg>
              {statsLoading ? "Loading…" : "Refresh All"}
            </button>
          </div>
        </div>

        {/* ── Global error ── */}
        {statsErr && <div className="vd-err">{statsErr}</div>}

        {/* ── Wallet Balance ── */}
        <div className="vd-wallet">
          <div className="vd-wallet-left">
            <div className="vd-wallet-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
            </div>
            <div>
              <div className="vd-wallet-title">Wallet Balance</div>
              <div className="vd-wallet-sub">Updated just now</div>
            </div>
          </div>
          <div className="vd-wallet-right">
            <div className="vd-wallet-value">
              {statsLoading
                ? <span className="vd-skeleton vd-skeleton-wallet" />
                : money(stats.walletBalance)
              }
            </div>
            <div className="vd-wallet-badge">Available</div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="vd-grid">
          {statsLoading
            ? [1, 2, 3].map((k) => <StatSkeleton key={k} />)
            : statCards.map((s) => (
                <div className={`vd-stat vd-stat-${s.color}`} key={s.title}>
                  <div className="vd-stat-top">
                    <div className="vd-stat-icon">{s.icon}</div>
                    <div className="vd-stat-title">{s.title}</div>
                  </div>
                  <div className="vd-stat-value">{s.value ?? "—"}</div>
                  <div className="vd-stat-bar">
                    <div className="vd-stat-bar-fill" />
                  </div>
                </div>
              ))
          }
        </div>

        {/* ── Tip ── */}
        <div className="vd-tip">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0}}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span><strong>Quick tip:</strong> Add Products → Receive Orders → Update Shipping → Request Payout</span>
        </div>

        {/* ── Analytics ── */}
        <div className="vd-analytics">

          {/* Chart Card */}
          <div className="vd-chart-card">
            <div className="vd-chart-head">
              <div>
                <div className="vd-chart-title">Monthly Orders</div>
                <div className="vd-chart-sub">Orders trend for your store</div>
              </div>
              <div className="vd-chart-tabs">
                <button className={"vd-tab " + (view === "12m" ? "active" : "")} onClick={() => setView("12m")}>
                  12 Months
                </button>
                <button className={"vd-tab " + (view === "6m" ? "active" : "")} onClick={() => setView("6m")}>
                  Last 6
                </button>
              </div>
            </div>

            {chartErr && (
              <div className="vd-err" style={{ margin: "0 18px 12px" }}>{chartErr}</div>
            )}

            <div className="vd-chart-body">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={filtered} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tickMargin={8} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fill="url(#ordersFill)"
                    dot={{ r: 3, fill: "#3b82f6", strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="vd-chart-footer">
              <button className="vd-action-btn" onClick={loadMonthly} disabled={loadingChart}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                </svg>
                {loadingChart ? "Loading…" : "Refresh Chart"}
              </button>
              <button className="vd-action-btn" onClick={loadWallet}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <line x1="2" y1="10" x2="22" y2="10"/>
                </svg>
                Refresh Wallet
              </button>
            </div>
          </div>

          {/* Mini Stats Card */}
          <div className="vd-mini-card">
            <div className="vd-mini-section">
              <div className="vd-mini-icon vd-mini-icon-gold">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <div className="vd-mini-label">Top Month</div>
              <div className="vd-mini-value">{top.orders}</div>
              <div className="vd-mini-sub">
                {top.name === "-" ? "No data yet" : `Highest in ${top.name}`}
              </div>
            </div>

            <div className="vd-mini-divider" />

            <div className="vd-mini-section">
              <div className="vd-mini-icon vd-mini-icon-blue">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <div className="vd-mini-label">Monthly Average</div>
              <div className="vd-mini-value">{avg}</div>
              <div className="vd-mini-sub">Avg orders per month</div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}